import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { checkInApi } from '@/api/safety'
import { PhoneIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline'

// Numeri di emergenza per paese.
//
// ⚠️ IL NUMERO DAN È UNO SOLO, UGUALE PER TUTTI I PAESI: +39 06 4211 5685.
//
// Verificato il 15/08/2026 su daneurope.org/emergency: DAN Europe dichiara un'unica
// linea internazionale, attiva 24 ore su 24 e valida "anywhere in the world", con prima
// risposta in inglese e inoltro a un medico che parla la lingua del chiamante. Non
// esistono linee di emergenza per paese.
//
// Qui c'erano sette numeri diversi, uno per paese, mai verificati contro una fonte
// ufficiale: probabili linee nazionali dismesse. Un numero morto in un'emergenza da
// decompressione e' peggio di una telefonata internazionale.
//
// ⚠️ NON sostituire questo numero con +39 085 893 0333. Quello e' l'ufficio Continental
// Operations di DAN, aperto lunedi'-venerdi' 9-17: era in 21 file dell'applicazione,
// compreso il protocollo di emergenza, e mandava i subacquei a chiamare un ufficio chiuso
// di notte e nel fine settimana. Corretto ovunque il 15/08/2026.
//
// Guardia costiera e ambulanza restano per paese: quelle sono davvero nazionali.
const DAN_EMERGENZA = '+39 06 4211 5685'

const EMERGENCY_NUMBERS: Record<string, { coast_guard?: string; ambulance: string; dan: string }> = {
  IT: { coast_guard: '1530', ambulance: '118', dan: DAN_EMERGENZA },
  HR: { coast_guard: '9155', ambulance: '112', dan: DAN_EMERGENZA },
  GR: { coast_guard: '108', ambulance: '166', dan: DAN_EMERGENZA },
  ES: { coast_guard: '900 202 202', ambulance: '112', dan: DAN_EMERGENZA },
  EG: { coast_guard: '122', ambulance: '123', dan: DAN_EMERGENZA },
  FR: { coast_guard: '196', ambulance: '15', dan: DAN_EMERGENZA },
  MT: { coast_guard: '21 250 520', ambulance: '112', dan: DAN_EMERGENZA },
  DEFAULT: { ambulance: '112', dan: DAN_EMERGENZA },
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return '00:00'
  const totalSec = Math.floor(ms / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m`
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

export default function EmergencyPanel() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [timeLeft, setTimeLeft] = useState<number | null>(null)
  const [dismissed, setDismissed] = useState(false)

  const { data: checkIn } = useQuery({
    queryKey: ['checkin', 'active'],
    queryFn: checkInApi.getActive,
    refetchInterval: 60_000,
    staleTime: 30_000,
  })

  const checkoutMutation = useMutation({
    mutationFn: () => checkInApi.checkout(checkIn!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['checkin', 'active'] })
    },
  })

  // Countdown timer
  useEffect(() => {
    if (!checkIn?.plannedEndAt) {
      setTimeLeft(null)
      return
    }
    const update = () => {
      const diff = new Date(checkIn.plannedEndAt!).getTime() - Date.now()
      setTimeLeft(Math.max(0, diff))
    }
    update()
    const interval = setInterval(update, 1000)
    return () => clearInterval(interval)
  }, [checkIn?.plannedEndAt])

  // Reset dismissed when check-in changes
  useEffect(() => {
    setDismissed(false)
  }, [checkIn?.id])

  if (!checkIn || dismissed) return null

  const country = checkIn.country?.toUpperCase() ?? 'DEFAULT'
  const numbers = EMERGENCY_NUMBERS[country] ?? EMERGENCY_NUMBERS.DEFAULT

  const isOverdue = timeLeft !== null && timeLeft === 0

  return (
    <div className={`fixed left-0 right-0 z-30 border-t shadow-2xl transition-colors bottom-16 lg:bottom-0
      ${isOverdue ? 'bg-red-900 border-red-700' : 'bg-gray-900 border-gray-700'}`}
    >
      <div className="max-w-screen-xl mx-auto px-4 py-3 flex flex-wrap items-center gap-3">
        {/* Status */}
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <ExclamationTriangleIcon className={`w-5 h-5 shrink-0 ${isOverdue ? 'text-red-400 animate-pulse' : 'text-amber-400'}`} />
          <div className="min-w-0">
            <span className="text-xs font-semibold text-white uppercase tracking-wide">
              {t('auto.componentsEmergencyPanel.checkInAttivo')}
            </span>
            {checkIn.diveSiteName && (
              <span className="text-xs text-gray-400 ml-2">{checkIn.diveSiteName}</span>
            )}
            {timeLeft !== null && (
              <span className={`text-xs ml-2 font-mono font-bold ${isOverdue ? 'text-red-400' : timeLeft < 300_000 ? 'text-orange-400' : 'text-emerald-400'}`}>
                {isOverdue ? 'SCADUTO' : formatCountdown(timeLeft)}
              </span>
            )}
          </div>
        </div>

        {/* Emergency numbers */}
        <div className="flex items-center gap-3 flex-wrap text-xs">
          <div className="flex items-center gap-1 text-gray-300">
            <PhoneIcon className="w-3.5 h-3.5" />
            <span>Ambulanza: <a href={`tel:${numbers.ambulance}`} className="font-bold text-white hover:underline">{numbers.ambulance}</a></span>
          </div>
          {numbers.coast_guard && (
            <div className="flex items-center gap-1 text-gray-300">
              <PhoneIcon className="w-3.5 h-3.5" />
              <span>Guardia Costiera: <a href={`tel:${numbers.coast_guard}`} className="font-bold text-white hover:underline">{numbers.coast_guard}</a></span>
            </div>
          )}
          <div className="flex items-center gap-1 text-gray-300">
            <PhoneIcon className="w-3.5 h-3.5" />
            <span>DAN: <a href={`tel:${numbers.dan}`} className="font-bold text-yellow-300 hover:underline">{numbers.dan}</a></span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => navigate('/app/safety')}
            className="px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white text-xs font-bold rounded-lg transition-colors"
          >
            SOS
          </button>
          <button
            onClick={() => checkoutMutation.mutate()}
            disabled={checkoutMutation.isPending}
            className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {checkoutMutation.isPending ? '...' : 'Check-out'}
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="p-2.5 text-gray-500 hover:text-gray-300 active:text-white transition-colors rounded-lg"
            title={t('auto.componentsEmergencyPanel.nascondiBanner')}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  )
}
